import { Link } from "react-router-dom";
import { APP_NAME } from "../lib/sport.js";

/**
 * Explanation of how the skill rating engine works.
 *
 * Written in clear, non-academic language for community members, matching the tone and
 * structure of Privacy.tsx.
 */

const UPDATED = "6 August 2026";

export function RatingSystem() {
  return (
    <main
      className="mx-auto max-w-2xl px-4 pb-24"
      style={{ paddingTop: "calc(env(safe-area-inset-top) + 2.5rem)" }}
    >
      <div className="micro mb-3">{APP_NAME}</div>
      <h1 className="font-display mb-2 text-3xl leading-tight" style={{ fontWeight: 800 }}>
        How Ratings Work
      </h1>
      <p className="micro mb-8">Last updated {UPDATED}</p>

      <div className="tramline mb-8" />

      <Section title="The short version">
        <p>
          {APP_NAME} uses an adapted Glicko-2 algorithm to calculate a trustworthy skill rating for
          every member. Your rating is displayed on a <strong>2.000 to 8.000</strong> scale. It
          adjusts after every confirmed match based on who won, the score margin, and how strong
          your opponents were.
        </p>
      </Section>

      <Section title="The two numbers: Rating & Reliability">
        <Row label="Skill Rating (2.00 – 8.00)">
          The primary number on the leaderboard showing your estimated skill level. A rating around
          2.5 is beginner, 3.5 is casual, 4.5 is solid club level, and 5.5+ is competitive
          tournament level.
        </Row>
        <Row label="Reliability %">
          How confident the system is in your rating. New players start near 0% reliability, so
          their rating moves fast. As you log more matches, your reliability reaches 100% and your
          rating stabilizes.
        </Row>
        <Row label="Provisional Status">
          Players with under 40% reliability are marked as <em>Provisional</em> on the board. This
          keeps new ratings out of official standings until they have settled (about 10 matches).
        </Row>
      </Section>

      <Section title="What changes your rating">
        <Row label="Winning vs. Losing">Winning increases your rating; losing decreases it.</Row>
        <Row label="Game & Point Margins">
          Every point counts! A decisive 21–5 win moves your rating more than a close 21–19 finish.
        </Row>
        <Row label="Opponent Strength">
          Beating a higher-rated opponent yields a larger rating gain than beating a lower-rated
          player. Conversely, an upset loss to a lower-rated player results in a larger drop.
        </Row>
        <Row label="Doubles Ratings">
          In doubles, team ratings combine both partners&rsquo; ratings. A win or loss adjusts both
          partners fairly based on the expected outcome between the two teams.
        </Row>
        <Row label="Mixed Matches Don&rsquo;t Count">
          A match with men and women on court is recorded, appears in everyone&rsquo;s history and
          counts towards your win–loss record, but it does not change anyone&rsquo;s rating. Only
          men&rsquo;s and women&rsquo;s matches move the number.
        </Row>
      </Section>

      <Section title="Starting point & Self-assessment">
        <Row label="Starting Level">
          When you join, your self-assessed level (New, Casual, Club, Competitive) sets your initial
          starting estimate between ~2.50 and ~5.25.
        </Row>
        <Row label="Self-Correction">
          Because new players start with 0% reliability, any over- or under-estimation corrects
          itself rapidly within your first ~10 matches. Sandbaggers and optimists converge to their
          true skill level very quickly.
        </Row>
      </Section>

      <Section title="Fairness & Integrity">
        <Row label="Disciplines & Sports">
          Singles and Doubles have independent ratings because skill sets differ. Badminton,
          Pickleball, and Table Tennis ratings are also completely separate.
        </Row>
        <Row label="Recalculated from Match Log">
          Ratings are calculated deterministically by replaying the confirmed match log. Neither
          players nor admins can manually edit rating numbers directly.
        </Row>
      </Section>

      <div className="tramline mb-8 mt-10" />
      <Link className="micro underline" to="/">
        Back
      </Link>
    </main>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="mb-10">
      {/* The section label the rest of the app uses — mono, upper case, on a rule. Set in
          the condensed display face at this size it read as cramped rather than headline. */}
      <h2
        className="font-mono mb-3 border-t-2 pt-3 text-[11px] font-semibold uppercase tracking-[0.12em] text-ink"
        style={{ borderColor: "var(--color-line)" }}
      >
        {title}
      </h2>
      <div className="text-ink-muted">{children}</div>
    </section>
  );
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="border-t py-3" style={{ borderColor: "var(--color-line-muted)" }}>
      <div className="micro mb-1" style={{ color: "var(--color-ink)" }}>
        {label}
      </div>
      <p>{children}</p>
    </div>
  );
}
